import React, { useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Camera, 
  Upload, 
  Plus, 
  Clock, 
  Calendar,
  MapPin,
  ArrowLeft,
  Sparkles,
  CheckCircle,
  XCircle,
  Edit3,
  Trash2,
  AlertCircle,
  ChevronRight
} from 'lucide-react';
import { featureFlags } from '../utils/featureFlags';
import { analytics } from '../utils/analytics';
import { scheduleOcrService } from '../services/ScheduleOcrService';
import { evidenceVaultService } from '../services/EvidenceVaultService';

interface ScheduleEntry {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  location?: string;
  notes?: string;
  confidence?: number; // For AI parsed entries
  verified: boolean;
}

interface ImportScheduleProps {
  onBack: () => void;
  onScheduleImported: (entries: ScheduleEntry[]) => void;
}

export function ImportSchedule({ onBack, onScheduleImported }: ImportScheduleProps) {
  const [importMethod, setImportMethod] = useState<'manual' | 'photo' | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedEntries, setParsedEntries] = useState<ScheduleEntry[]>([]);
  const [showResults, setShowResults] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Manual entry form state
  const [manualEntry, setManualEntry] = useState({
    date: '',
    startTime: '',
    endTime: '',
    location: '',
    notes: ''
  });

  const resetForm = useCallback(() => {
    setManualEntry({
      date: '',
      startTime: '',
      endTime: '',
      location: '',
      notes: ''
    });
  }, []);

  const handleManualSubmit = useCallback(() => {
    if (!manualEntry.date || !manualEntry.startTime || !manualEntry.endTime) {
      return;
    }

    const newEntry: ScheduleEntry = {
      id: Date.now().toString(),
      date: manualEntry.date,
      startTime: manualEntry.startTime,
      endTime: manualEntry.endTime,
      location: manualEntry.location,
      notes: manualEntry.notes,
      verified: true
    };

    setParsedEntries([newEntry]);
    setShowResults(true);
  }, [manualEntry]);

  // REMOVED: mockAIParse function - REPLACED WITH REAL OCR!
  // See scheduleOcrService.parseScheduleFromImage() for real OCR implementation

  const handleFileUpload = useCallback(async (file: File) => {
    if (!file) return;

    setIsProcessing(true);
    analytics.track('schedule_photo_uploaded', { file_type: file.type });
    
    try {
      console.log('🔍 Starting REAL OCR parsing (not mock data)...');
      
      // 1. Parse schedule using real Tesseract.js OCR
      const results = await scheduleOcrService.parseScheduleFromImage(file);
      
      // 2. Store photo in evidence vault for legal protection
      try {
        await evidenceVaultService.storeEvidence({
          photoFile: file,
          documentType: 'schedule',
          notes: 'Imported schedule photo',
        });
        console.log('✅ Photo stored in evidence vault');
      } catch (error) {
        console.warn('⚠️ Evidence vault storage failed (continuing anyway):', error);
      }
      
      setParsedEntries(results);
      setShowResults(true);
      analytics.track('ai_schedule_parsed', { entries_found: results.length });
      
      if (results.length === 0) {
        // Show helpful message when no entries detected
        alert('No schedule entries detected. Try manual entry or retake photo with better lighting.');
      }
    } catch (error) {
      console.error('Error processing file:', error);
      const err = error as any;
      analytics.track('schedule_parse_error', { error: err?.message || String(err) });
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const handleCameraCapture = useCallback(() => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  }, []);

  const handleFileSelect = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  }, [handleFileUpload]);

  const updateEntry = useCallback((id: string, updates: Partial<ScheduleEntry>) => {
    setParsedEntries(prev => 
      prev.map(entry => 
        entry.id === id ? { ...entry, ...updates } : entry
      )
    );
  }, []);

  const removeEntry = useCallback((id: string) => {
    setParsedEntries(prev => prev.filter(entry => entry.id !== id));
  }, []);

  const verifyEntry = useCallback((id: string) => {
    updateEntry(id, { verified: true });
  }, [updateEntry]);

  const handleImportAll = useCallback(() => {
    const verifiedEntries = parsedEntries.filter(entry => entry.verified);
  analytics.trackScheduleImport((importMethod as any) || 'file', verifiedEntries.length);
    onScheduleImported(verifiedEntries);
    onBack();
  }, [parsedEntries, onScheduleImported, onBack, importMethod]);

  const getConfidenceColor = (confidence?: number) => {
    if (!confidence) return '';
    if (confidence >= 0.9) return 'text-green-600';
    if (confidence >= 0.7) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getConfidenceBadge = (confidence?: number) => {
    if (!confidence) return null;
    if (confidence >= 0.9) return <Badge variant="secondary" className="bg-green-100 text-green-800">High</Badge>;
    if (confidence >= 0.7) return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Medium</Badge>;
    return <Badge variant="secondary" className="bg-red-100 text-red-800">Low</Badge>;
  };

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-background p-4 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center space-y-6">
            <div className="w-16 h-16 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">AI Processing Schedule</h3>
              <p className="text-sm text-muted-foreground">
                Analyzing your image and extracting shift information...
              </p>
            </div>
            <div className="space-y-1 text-xs text-muted-foreground">
              <div className="flex items-center justify-center space-x-2">
                <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                <span>Reading text from image</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <div className="w-2 h-2 bg-primary/60 rounded-full animate-pulse animation-delay-300"></div>
                <span>Identifying dates and times</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <div className="w-2 h-2 bg-primary/40 rounded-full animate-pulse animation-delay-600"></div>
                <span>Extracting locations and notes</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="min-h-screen bg-background p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setShowResults(false)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Review Schedule</h1>
            <p className="text-muted-foreground">
              {parsedEntries.length} shift{parsedEntries.length !== 1 ? 's' : ''} found
            </p>
          </div>
        </div>

        {/* AI Confidence Alert */}
        {parsedEntries.some(entry => entry.confidence) && (
          <Alert>
            <Sparkles className="h-4 w-4" />
            <AlertDescription>
              These shifts were automatically detected. Please review each one for accuracy before importing.
            </AlertDescription>
          </Alert>
        )}

        {/* Parsed Entries */}
        <div className="space-y-4">
          {parsedEntries.map((entry) => (
            <Card key={entry.id} className={`transition-all ${entry.verified ? 'ring-2 ring-green-500' : ''}`}>
              <CardContent className="p-4">
                <div className="space-y-4">
                  {/* Entry Header */}
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">
                          {new Date(entry.date).toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span>{entry.startTime} - {entry.endTime}</span>
                      </div>
                      {entry.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">{entry.location}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {entry.confidence && getConfidenceBadge(entry.confidence)}
                      {entry.verified ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => verifyEntry(entry.id)}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeEntry(entry.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Entry Details */}
                  {entry.notes && (
                    <p className="text-sm text-muted-foreground bg-muted p-2 rounded">
                      {entry.notes}
                    </p>
                  )}

                  {/* Confidence Warning */}
                  {entry.confidence && entry.confidence < 0.8 && !entry.verified && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Low confidence detection. Please verify the times and location are correct.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Import Actions */}
        <div className="sticky bottom-4 bg-background border border-border rounded-lg p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">
                {parsedEntries.filter(e => e.verified).length} of {parsedEntries.length} verified
              </p>
              <p className="text-sm text-muted-foreground">
                Only verified shifts will be imported
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowResults(false)}>
                Back
              </Button>
              <Button 
                onClick={handleImportAll}
                disabled={parsedEntries.filter(e => e.verified).length === 0}
              >
                Import {parsedEntries.filter(e => e.verified).length} Shift{parsedEntries.filter(e => e.verified).length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (importMethod === 'photo') {
    return (
      <div className="min-h-screen bg-background p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => setImportMethod(null)} className="h-8 w-8 p-0">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Photo Import</h1>
            <p className="text-muted-foreground">Choose how to capture your schedule</p>
          </div>
        </div>

        {/* Photo Options */}
        <div className="space-y-4">
          {/* Camera Capture */}
          <Card 
            className="clock-it-card cursor-pointer hover:shadow-lg transition-all duration-200 border-primary/20" 
            onClick={handleCameraCapture}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-primary/10 rounded-xl">
                  <Camera className="w-8 h-8 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-2">Take Photo Now</h3>
                  <p className="text-sm text-muted-foreground">
                    Use your device camera to photograph your schedule directly
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          {/* Upload from Gallery */}
          <Card 
            className="clock-it-card cursor-pointer hover:shadow-lg transition-all duration-200" 
            onClick={handleFileSelect}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-secondary/20 rounded-xl">
                  <Upload className="w-8 h-8 text-secondary-foreground" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-2">Choose from Gallery</h3>
                  <p className="text-sm text-muted-foreground">
                    Select an existing photo of your schedule from your device
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Photo Tips */}
        <Card className="clock-it-card bg-muted/30 border-muted/50">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center">
                <Camera className="w-3 h-3 text-primary" />
              </div>
              <h4 className="font-semibold text-foreground">Photo Tips for Best AI Results</h4>
            </div>
            <ul className="text-sm text-muted-foreground space-y-2 ml-7">
              <li className="flex items-start gap-2">
                <div className="w-1 h-1 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                <span>Hold device steady and ensure good lighting</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1 h-1 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                <span>Capture the entire schedule in frame</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1 h-1 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                <span>Avoid shadows and reflections on the text</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1 h-1 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                <span>Make sure all text is clearly readable</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (importMethod === 'manual') {
    const calculateHours = () => {
      if (manualEntry.startTime && manualEntry.endTime) {
        const start = new Date(`2000-01-01T${manualEntry.startTime}`);
        const end = new Date(`2000-01-01T${manualEntry.endTime}`);
        const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        return hours > 0 ? hours.toFixed(1) : null;
      }
      return null;
    };

    return (
      <div className="min-h-screen bg-background p-4 space-y-6">
        {/* Professional Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => setImportMethod(null)} className="h-8 w-8 p-0">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Add Shift Manually</h1>
            <p className="text-muted-foreground">Enter your shift details with precision</p>
          </div>
        </div>

        {/* Enhanced Manual Entry Form */}
        <Card className="clock-it-card">
          <CardContent className="p-6 space-y-6">
            {/* Date Input */}
            <div className="space-y-3">
              <Label htmlFor="date" className="text-sm font-semibold text-foreground">
                Date <span className="text-destructive">*</span>
              </Label>
              <Input
                id="date"
                type="date"
                value={manualEntry.date}
                onChange={(e) => setManualEntry(prev => ({ ...prev, date: e.target.value }))}
                className="h-12 bg-input-background border-border"
              />
            </div>

            {/* Time Inputs */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <Label htmlFor="startTime" className="text-sm font-semibold text-foreground">
                  Start Time <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="startTime"
                  type="time"
                  value={manualEntry.startTime}
                  onChange={(e) => setManualEntry(prev => ({ ...prev, startTime: e.target.value }))}
                  className="h-12 bg-input-background border-border"
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="endTime" className="text-sm font-semibold text-foreground">
                  End Time <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="endTime"
                  type="time"
                  value={manualEntry.endTime}
                  onChange={(e) => setManualEntry(prev => ({ ...prev, endTime: e.target.value }))}
                  className="h-12 bg-input-background border-border"
                />
              </div>
            </div>

            {/* Hours Calculation Display */}
            {calculateHours() && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">
                    Shift Duration: {calculateHours()} hours
                  </span>
                </div>
              </div>
            )}

            {/* Location Input */}
            <div className="space-y-3">
              <Label htmlFor="location" className="text-sm font-semibold text-foreground">
                Location
              </Label>
              <Input
                id="location"
                placeholder="e.g., Main Store, Downtown Location, Customer Service"
                value={manualEntry.location}
                onChange={(e) => setManualEntry(prev => ({ ...prev, location: e.target.value }))}
                className="h-12 bg-input-background border-border"
              />
            </div>

            {/* Notes Input */}
            <div className="space-y-3">
              <Label htmlFor="notes" className="text-sm font-semibold text-foreground">
                Notes
              </Label>
              <Textarea
                id="notes"
                placeholder="e.g., Cover shift, Holiday hours, Training session"
                value={manualEntry.notes}
                onChange={(e) => setManualEntry(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="bg-input-background border-border resize-none"
              />
            </div>
          </CardContent>
        </Card>

        {/* Form Validation Messages */}
        {(!manualEntry.date || !manualEntry.startTime || !manualEntry.endTime) && (
          <Card className="clock-it-card bg-muted/30 border-muted/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Please fill in all required fields (Date, Start Time, End Time)
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <Button 
            variant="outline" 
            onClick={resetForm} 
            className="h-12 font-medium"
          >
            Clear Form
          </Button>
          <Button 
            onClick={handleManualSubmit}
            className="h-12 font-medium bg-primary hover:bg-primary/90"
            disabled={!manualEntry.date || !manualEntry.startTime || !manualEntry.endTime}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Shift
          </Button>
        </div>
      </div>
    );
  }

  // Enhanced import method selection screen
  return (
    <div className="min-h-screen bg-background p-4 space-y-6">
      {/* Professional Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={onBack} className="h-8 w-8 p-0">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Import Schedule</h1>
          <p className="text-muted-foreground">Add your work schedule to Clock-It</p>
        </div>
      </div>

      {/* Enhanced Import Options */}
      <div className="space-y-4">
        {/* AI Photo Import - Primary Option */}
        <Card 
          className="clock-it-card cursor-pointer hover:shadow-lg transition-all duration-200 border-primary/20" 
          onClick={() => setImportMethod('photo')}
        >
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-4 bg-primary/10 rounded-xl">
                <Camera className="w-7 h-7 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground flex items-center gap-2 mb-2">
                  Take Photo of Schedule
                  <Sparkles className="w-4 h-4 text-primary" />
                </h3>
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                  Snap a photo of your printed schedule and let our advanced AI extract all shift information automatically with high accuracy
                </p>
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary/10 text-primary border-primary/20 text-xs">AI Powered</Badge>
                  <Badge variant="outline" className="text-xs">Fastest</Badge>
                  <Badge variant="outline" className="text-xs">Recommended</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Manual Entry */}
        <Card 
          className="clock-it-card cursor-pointer hover:shadow-lg transition-all duration-200" 
          onClick={() => setImportMethod('manual')}
        >
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-4 bg-secondary/20 rounded-xl">
                <Edit3 className="w-7 h-7 text-secondary-foreground" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2">Enter Manually</h3>
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                  Type in your shift details manually for complete control and accuracy. Perfect for complex schedules
                </p>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">Most Accurate</Badge>
                  <Badge variant="outline" className="text-xs">Full Control</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload File Option */}
        <Card 
          className="clock-it-card cursor-pointer hover:shadow-lg transition-all duration-200" 
          onClick={handleFileSelect}
        >
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-4 bg-accent/20 rounded-xl">
                <Upload className="w-7 h-7 text-accent-foreground" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2">Upload Digital File</h3>
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                  Upload a digital schedule file (PDF, image, or CSV) for AI processing and extraction
                </p>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">PDF, JPG, PNG, CSV</Badge>
                  <Badge variant="outline" className="text-xs">Digital Files</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Professional Tips Section */}
      <Card className="clock-it-card bg-muted/30 border-muted/50">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-primary" />
            </div>
            <h4 className="font-semibold text-foreground">Pro Tips for Best Results</h4>
          </div>
          <ul className="text-sm text-muted-foreground space-y-2 ml-7">
            <li className="flex items-start gap-2">
              <div className="w-1 h-1 bg-primary rounded-full mt-2 flex-shrink-0"></div>
              <span>Ensure schedule text is clear and well-lit in photos</span>
            </li>
            <li className="flex items-start gap-2">
              <div className="w-1 h-1 bg-primary rounded-full mt-2 flex-shrink-0"></div>
              <span>Include all shift details (dates, times, locations)</span>
            </li>
            <li className="flex items-start gap-2">
              <div className="w-1 h-1 bg-primary rounded-full mt-2 flex-shrink-0"></div>
              <span>Review AI-extracted information before importing</span>
            </li>
            <li className="flex items-start gap-2">
              <div className="w-1 h-1 bg-primary rounded-full mt-2 flex-shrink-0"></div>
              <span>Use manual entry for complex schedules with custom formatting</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf,.csv"
        onChange={handleFileChange}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}
